import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CriteriaGroup } from '@/types/evaluation.types';
import { Importance } from '@/types/evaluation.types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Star, Info, Minus } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';

const importanceIcons = {
  [Importance.Critical]: <Star className="h-3 w-3 text-red-600" aria-hidden="true" />,
  [Importance.High]: <AlertTriangle className="h-3 w-3 text-orange-600" aria-hidden="true" />,
  [Importance.Medium]: <AlertTriangle className="h-3 w-3 text-yellow-600" aria-hidden="true" />,
  [Importance.Low]: <Info className="h-3 w-3 text-blue-600" aria-hidden="true" />,
  [Importance.None]: <Minus className="h-3 w-3 text-gray-400" aria-hidden="true" />,
};

interface WeightDistributionProps {
  groups: CriteriaGroup[];
  compact?: boolean;
}

export function WeightDistribution({ groups, compact = false }: WeightDistributionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { t } = useTranslation('criteria');

  const totalWeight = groups.reduce((sum, group) => sum + group.weight, 0);
  const isValid = totalWeight === 100;
  const isEmpty = groups.length === 0;

  if (isEmpty) {
    return null;
  }

  // Compact mode - inline bar with expand button
  if (compact) {
    return (
      <div className="space-y-3">
        {/* Compact bar */}
        <div className="flex items-center gap-4">
          <div className="flex-1 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-2">
                <span className="font-medium">{t('weightDistribution.label')}</span>
                {isValid ? (
                  <CheckCircle className="h-4 w-4 text-green-600" aria-hidden="true" />
                ) : (
                  <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />
                )}
              </div>
              <span
                className={isValid ? 'text-green-600 font-medium' : 'text-destructive font-medium'}
                aria-label={t('weightDistribution.totalWeight', { total: totalWeight })}
              >
                {totalWeight}%
              </span>
            </div>
            <Progress
              value={Math.min(totalWeight, 100)}
              aria-label={t('weightDistribution.progressLabel')}
              className={totalWeight > 100 ? '[&>div]:bg-destructive' : ''}
            />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            aria-label={isExpanded ? t('common:actions.collapse') : t('common:actions.expand')}
            aria-expanded={isExpanded}
          >
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            )}
          </Button>
        </div>

        {/* Expanded details */}
        {isExpanded && (
          <div className="space-y-3 pt-3 border-t">
            {!isValid && (
              <Alert variant={totalWeight > 100 ? 'destructive' : 'warning'} role="alert">
                {totalWeight === 100 ? (
                  <CheckCircle className="h-4 w-4" aria-hidden="true" />
                ) : (
                  <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                )}
                <AlertDescription>
                  {totalWeight < 100
                    ? t('weightDistribution.underweightWarning', {
                        remaining: 100 - totalWeight,
                      })
                    : t('weightDistribution.overweightWarning', {
                        excess: totalWeight - 100,
                      })}
                </AlertDescription>
              </Alert>
            )}

            {isValid && (
              <Alert role="status">
                <CheckCircle className="h-4 w-4 text-green-600" aria-hidden="true" />
                <AlertDescription className="text-green-600 font-medium">
                  {t('weightDistribution.validMessage')}
                </AlertDescription>
              </Alert>
            )}

            {/* Individual group weights */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                {t('weightDistribution.breakdown')}
              </p>
              <div className="space-y-1">
                {groups.map((group) => (
                  <div
                    key={group.id}
                    className="flex items-center justify-between text-sm"
                  >
                    <span className="text-muted-foreground truncate flex-1">
                      {group.name}
                    </span>
                    <span className="font-medium ml-2 tabular-nums">
                      {group.weight}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Importance Legend */}
            <div className="space-y-2 pt-3 border-t">
              <p className="text-sm font-medium text-muted-foreground">
                {t('weightDistribution.importanceLegend')}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {Object.values(Importance).map((importance) => (
                  <div key={importance} className="flex items-center gap-2 text-sm">
                    {importanceIcons[importance]}
                    <span className="text-muted-foreground">
                      {t(`form.importance.levels.${importance.toLowerCase()}`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Full mode (for sidebar or other uses)
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">{t('weightDistribution.label')}</span>
          <span
            className={isValid ? 'text-green-600 font-medium' : 'text-destructive font-medium'}
            aria-label={t('weightDistribution.totalWeight', { total: totalWeight })}
          >
            {totalWeight}%
          </span>
        </div>
        <Progress
          value={Math.min(totalWeight, 100)}
          aria-label={t('weightDistribution.progressLabel')}
          className={totalWeight > 100 ? '[&>div]:bg-destructive' : ''}
        />
      </div>

      {!isValid && (
        <Alert variant={totalWeight > 100 ? 'destructive' : 'warning'} role="alert">
          {totalWeight === 100 ? (
            <CheckCircle className="h-4 w-4" aria-hidden="true" />
          ) : (
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          )}
          <AlertDescription>
            {totalWeight < 100
              ? t('weightDistribution.underweightWarning', {
                  remaining: 100 - totalWeight,
                })
              : t('weightDistribution.overweightWarning', {
                  excess: totalWeight - 100,
                })}
          </AlertDescription>
        </Alert>
      )}

      {isValid && groups.length > 0 && (
        <Alert role="status">
          <CheckCircle className="h-4 w-4 text-green-600" aria-hidden="true" />
          <AlertDescription className="text-green-600 font-medium">
            {t('weightDistribution.validMessage')}
          </AlertDescription>
        </Alert>
      )}

      {/* Individual group weights */}
      {groups.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">
            {t('weightDistribution.breakdown')}
          </p>
          <div className="space-y-1">
            {groups.map((group) => (
              <div
                key={group.id}
                className="flex items-center justify-between text-sm"
              >
                <span className="text-muted-foreground truncate flex-1">
                  {group.name}
                </span>
                <span className="font-medium ml-2 tabular-nums">
                  {group.weight}%
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
